import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Alert,
  Tabs,
  Tab,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetEnstruLookupQuery, useGetSpecQuery, useGetPlaybookQuery } from '@/services/api';
import Editor from '@monaco-editor/react';
import type { SpecProperty } from '@/types/api';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`spec-tabpanel-${index}`}
      aria-labelledby={`spec-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

const SpecViewer: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { language } = useSelector((state: RootState) => state.ui);

  const {
    data: spec,
    error,
    isLoading,
  } = useGetSpecQuery(lotId!, { skip: !lotId });
  const { data: enstruLookup } = useGetEnstruLookupQuery();

  // Extract category ID from spec category and fetch playbook
  const categoryId = spec?.category?.replace(/^ENSTRU:/, '');
  const enstruDetails = categoryId ? enstruLookup?.[categoryId] : undefined;
  const {
    data: playbook,
    error: playbookError,
    isLoading: isLoadingPlaybook,
  } = useGetPlaybookQuery(
    { category: categoryId! },
    { skip: !categoryId }
  );

  // Helper function to get synonym suggestions for a property
  const getSynonymSuggestions = (propertyKey: string) => {
    if (!spec?.playbook_suggestions?.synonym_suggestions) return [];
    
    return spec.playbook_suggestions.synonym_suggestions
      .filter((suggestion: any) => suggestion.target_ref === propertyKey)
      .flatMap((suggestion: any) => suggestion.new_synonyms?.[language] || []);
  };

  // Group properties by aspects and blocks for a specific item
  const getGroupedPropertiesForItem = useMemo(() => {
    return (itemId: string) => {
      if (!spec || !playbook || !playbook.property_library) return [];

      const item = spec.items.find(i => i.item_id === itemId);
      if (!item) return [];

      // Collect properties from the specific item
      const itemProperties = new Map<string, { property: SpecProperty; items: string[] }>();
      
      Object.entries(item.properties).forEach(([key, property]) => {
        itemProperties.set(key, { property, items: [item.item_id] });
      });

      // Filter properties by search term
      const filteredProperties = Array.from(itemProperties.entries()).filter(([key, data]) => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const keyMatch = key.toLowerCase().includes(searchLower);
        
        // Check playbook property name
        const playbookProperty = playbook.property_library[key];
        const nameMatch = playbookProperty ? 
          (playbookProperty.preferred_name[language] || playbookProperty.preferred_name.en || '').toLowerCase().includes(searchLower) : false;
        
        const synonymsMatch = playbookProperty?.synonyms?.[language]?.some(synonym => 
          synonym.toLowerCase().includes(searchLower)
        ) || false;
        
        return keyMatch || nameMatch || synonymsMatch;
      });

    // Group properties by aspects and blocks
    const groups: Array<{
      aspectId: string;
      aspectName: string;
      aspectDescription?: string;
      blocks: Array<{
        blockId: string;
        blockName: string;
        properties: Array<[string, { property: SpecProperty; items: string[] }]>;
      }>;
    }> = [];

    // Create a map of filtered properties
    const propertyMap = new Map(filteredProperties);
    
    // Process each aspect from playbook
    (playbook.aspects || []).forEach(aspect => {
      const aspectGroup = {
        aspectId: aspect.id,
        aspectName: aspect.name[language] || aspect.name.en,
        aspectDescription: aspect.description?.[language] || aspect.description?.en,
        blocks: [] as Array<{
          blockId: string;
          blockName: string;
          properties: Array<[string, { property: SpecProperty; items: string[] }]>;
        }>
      };

      // Process each block in the aspect
      aspect.blocks.forEach(block => {
        const blockProperties = block.properties
          .map(prop => [prop.ref, propertyMap.get(prop.ref)])
          .filter(([, data]) => data) as Array<[string, { property: SpecProperty; items: string[] }]>;

        if (blockProperties.length > 0) {
          aspectGroup.blocks.push({
            blockId: block.id,
            blockName: block.name[language] || block.name.en,
            properties: blockProperties
          });
        }
      });

      if (aspectGroup.blocks.length > 0) {
        groups.push(aspectGroup);
      }
    });

    // Add ungrouped properties (not in any aspect/block)
    const groupedPropertyRefs = new Set();
    groups.forEach(group => 
      group.blocks.forEach(block => 
        block.properties.forEach(([ref]) => groupedPropertyRefs.add(ref))
      )
    );

    const ungroupedProperties = filteredProperties.filter(([key]) => !groupedPropertyRefs.has(key));
    
    if (ungroupedProperties.length > 0) {
      groups.push({
        aspectId: 'ungrouped',
        aspectName: language === 'en' ? 'Other Properties' : 'Прочие свойства',
        aspectDescription: language === 'en' ? 'Properties not organized into specific aspects' : 'Свойства, не организованные в конкретные аспекты',
        blocks: [{
          blockId: 'ungrouped-block',
          blockName: language === 'en' ? 'General' : 'Общие',
          properties: ungroupedProperties
        }]
      });
    }

    // Add Additional Properties from playbook_suggestions
    if (spec.playbook_suggestions && (spec.playbook_suggestions.candidate_properties?.length > 0 || spec.playbook_suggestions.candidate_value_list_items?.length > 0)) {
      const additionalProperties: Array<[string, { property: SpecProperty; items: string[]; displayName?: string }]> = [];
      
      // Add candidate properties
      if (spec.playbook_suggestions.candidate_properties) {
        spec.playbook_suggestions.candidate_properties.forEach((suggestion: any) => {
          const displayName = suggestion.display_names?.[language] || 
                            suggestion.display_names?.ru || 
                            suggestion.display_names?.en || 
                            suggestion.suggested_name || 
                            suggestion.proposed_ref;
          
          // Filter by search term if specified
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const keyMatch = (suggestion.proposed_ref || suggestion.property_key || '').toLowerCase().includes(searchLower);
            const nameMatch = (displayName || '').toLowerCase().includes(searchLower);
            if (!keyMatch && !nameMatch) return;
          }

          // Create value from value_example if available
          let displayValue = 'N/A';
          if (suggestion.value_example) {
            if (suggestion.value_example.value !== undefined) {
              displayValue = `${suggestion.value_example.value}${suggestion.value_example.unit ? ` ${suggestion.value_example.unit}` : ''}`;
            }
          }

          const syntheticProperty: SpecProperty = {
            type: suggestion.data_type || 'text',
            kind: suggestion.kind,
            value: displayValue,
            evidence: suggestion.evidence || [{ text: 'Suggested by AI' }],
            confidence: suggestion.confidence || 0.5
          };
          
          additionalProperties.push([
            suggestion.proposed_ref || suggestion.property_key || `suggestion_${additionalProperties.length}`,
            { 
              property: syntheticProperty, 
              items: [item.item_id],
              displayName: displayName
            }
          ]);
        });
      }

      // Add candidate value list items
      if (spec.playbook_suggestions.candidate_value_list_items) {
        spec.playbook_suggestions.candidate_value_list_items.forEach((suggestion: any) => {
          // Filter by search term if specified
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const keyMatch = (suggestion.property_key || '').toLowerCase().includes(searchLower);
            const valueMatch = (suggestion.suggested_value || '').toLowerCase().includes(searchLower);
            if (!keyMatch && !valueMatch) return;
          }

          const syntheticProperty: SpecProperty = {
            type: 'enum',
            value: suggestion.suggested_value || 'N/A',
            evidence: suggestion.evidence || [{ text: 'Value list suggestion' }],
            confidence: suggestion.confidence || 0.5
          };
          
          additionalProperties.push([
            suggestion.property_key || `value_list_${additionalProperties.length}`,
            { property: syntheticProperty, items: [item.item_id] }
          ]);
        });
      }

      if (additionalProperties.length > 0) {
        groups.push({
          aspectId: 'additional-properties',
          aspectName: language === 'en' ? 'Additional Properties' : 'Дополнительные свойства',
          aspectDescription: language === 'en' ? 'Properties and values suggested by AI analysis (not in current playbook)' : 'Свойства и значения, предложенные ИИ-анализом (не в текущем справочнике)',
          blocks: [{
            blockId: 'suggestions-block',
            blockName: language === 'en' ? 'AI Suggestions' : 'Предложения ИИ',
            properties: additionalProperties
          }]
        });
      }
    }

      return groups;
    };
  }, [spec, playbook, language, searchTerm]);

  // Calculate total and filtered property counts for active item
  const activeItem = spec?.items[activeTab];
  const totalProperties = activeItem ? Object.keys(activeItem.properties).length : 0;
  const groupedProperties = getGroupedPropertiesForItem(activeItem?.item_id || '');
  const filteredCount = groupedProperties.reduce((sum, group) => 
    sum + group.blocks.reduce((blockSum, block) => blockSum + block.properties.length, 0), 0
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'success';
    if (confidence >= 0.7) return 'warning';
    return 'error';
  };

  const formatPropertyValue = (property: SpecProperty) => {
    if (property.type === 'measure') {
      return `${property.value}${property.unit ? ` ${property.unit}` : ''}`;
    }
    if (property.type === 'enum') {
      return property.value;
    }
    return String(property.value);
  };

  if (isLoading || isLoadingPlaybook) {
    return (
      <Container maxWidth={false} sx={{ px: 3 }}>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Container>
    );
  }

  if (error) {
    const errorMessage = 'data' in error && error.data 
      ? (error.data as any).message || 'Failed to load specification'
      : 'Failed to load specification';

    return (
      <Container maxWidth={false} sx={{ px: 3 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Error Loading Specification
          </Typography>
          <Typography variant="body2">
            {errorMessage}
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!spec) {
    return (
      <Container maxWidth={false} sx={{ px: 3 }}>
        <Alert severity="warning" sx={{ mt: 2 }}>
          Specification not found: {lotId}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ px: 3 }}>

      {/* Header */}
      <Box sx={{ pt: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getValidationIcon(spec.validation.status)}
          <Typography variant="h4" component="h1" sx={{ ml: 1 }}>
            ID {spec.spec_id}{playbook?.category?.class?.preferred_name ? `: ${
              playbook.category.class.preferred_name[language] || 
              playbook.category.class.preferred_name.en || 
              playbook.category.class.preferred_name.ru
            }` : ''}
          </Typography>
        </Box>

        {/* Metadata table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>{language === 'en' ? 'Playbook Version' : 'Версия справочника'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Category Code' : 'Код категории'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Items' : 'Элементы'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Parsed At' : 'Дата парсинга'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'LLM Model' : 'Модель LLM'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Playbook Properties' : 'Свойства справочника'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Additional Properties' : 'Доп. свойства'}</strong></TableCell>
                <TableCell><strong>{language === 'en' ? 'Validation' : 'Валидация'}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{spec.playbook_version || '-'}</TableCell>
                <TableCell>
                  {spec.category ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {categoryId}
                      </Typography>
                      {enstruDetails?.name && (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {enstruDetails.name}
                        </Typography>
                      )}
                      {enstruDetails?.characteristic && (
                        <Typography variant="caption" color="text.secondary">
                          {enstruDetails.characteristic}
                        </Typography>
                      )}
                    </Box>
                  ) : '-'}
                </TableCell>
                <TableCell>{spec.items.length}</TableCell>
                <TableCell>
                  {(() => {
                    try {
                      // Parse the date string properly - if no timezone, assume UTC
                      const dateStr = spec.parsed_at;
                      // Remove milliseconds if present
                      const cleanDateStr = dateStr.replace(/\.\d{3}/, '');
                      const date = cleanDateStr.includes('T') && !cleanDateStr.includes('Z') && !cleanDateStr.includes('+') 
                        ? new Date(cleanDateStr + 'Z') // Add Z to treat as UTC
                        : new Date(cleanDateStr);
                      
                      if (isNaN(date.getTime())) {
                        // Try alternative parsing
                        const fallbackDate = new Date(spec.parsed_at);
                        if (isNaN(fallbackDate.getTime())) return '-';
                        
                        const year = fallbackDate.getFullYear();
                        const month = String(fallbackDate.getMonth() + 1).padStart(2, '0');
                        const day = String(fallbackDate.getDate()).padStart(2, '0');
                        const hours = String(fallbackDate.getHours()).padStart(2, '0');
                        const minutes = String(fallbackDate.getMinutes()).padStart(2, '0');
                        const seconds = String(fallbackDate.getSeconds()).padStart(2, '0');
                        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                      }
                      
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const hours = String(date.getHours()).padStart(2, '0');
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const seconds = String(date.getSeconds()).padStart(2, '0');
                      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                    } catch (error) {
                      return '-';
                    }
                  })()}
                </TableCell>
                <TableCell>{(spec as any).extraction_meta?.llm_model || '-'}</TableCell>
                <TableCell>
                  {(() => {
                    // Count total playbook properties across all items
                    const activeItem = spec?.items[activeTab];
                    if (!activeItem) return '0';
                    
                    let totalPlaybookProps = 0;
                    const groupedProperties = getGroupedPropertiesForItem(activeItem.item_id);
                    
                    groupedProperties.forEach(group => {
                      if (group.aspectId !== 'additional-properties') {
                        group.blocks.forEach(block => {
                          totalPlaybookProps += block.properties.length;
                        });
                      }
                    });
                    
                    return totalPlaybookProps;
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    // Count additional properties from playbook suggestions
                    const candidateProps = spec.playbook_suggestions?.candidate_properties?.length || 0;
                    const candidateValues = spec.playbook_suggestions?.candidate_value_list_items?.length || 0;
                    return candidateProps + candidateValues;
                  })()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={spec.validation.status}
                      size="small"
                      color={spec.validation.status === 'ok' ? 'success' : 'error'}
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      missing: {spec.validation.missing_required.length}, violations: {spec.validation.enum_violations.length}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="spec tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {spec.items.map((item, index) => (
              <Tab 
                key={item.item_id} 
                label={`Item ${item.item_id}${item.quantity ? ` (${item.quantity})` : ''}`} 
              />
            ))}
            <Tab label="Validation" />
            <Tab label="Full JSON" />
          </Tabs>
        </Box>

        {/* Item Tabs */}
        {spec.items.map((item, index) => (
          <TabPanel key={item.item_id} value={activeTab} index={index}>
            <Box sx={{ width: '100%' }}>
              {/* Item Header */}
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Item {item.item_id}
                  {item.quantity && (
                    <Chip
                      label={`Quantity: ${item.quantity}`}
                      size="small"
                      sx={{ ml: 2 }}
                      color="primary"
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Object.keys(item.properties).length} properties
                </Typography>
              </Box>

              {/* Search and Count Header */}
              <Box sx={{ mb: 3, mt: 2, px: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={language === 'en' ? 'Search properties...' : 'Поиск свойств...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ maxWidth: 400 }}
                />
                <Box sx={{ ml: 'auto' }}>
                  <Chip
                    label={searchTerm 
                      ? (language === 'en' ? `${filteredCount} of ${totalProperties} properties` : `${filteredCount} из ${totalProperties} свойств`)
                      : (language === 'en' ? `${totalProperties} properties` : `${totalProperties} свойств`)
                    }
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Grouped Properties */}
              {groupedProperties.length === 0 ? (
                <Alert severity="info">
                  {searchTerm 
                    ? (language === 'en' ? 'No properties match your search.' : 'Свойства не найдены по вашему запросу.')
                    : (language === 'en' ? 'No properties found.' : 'Свойства не найдены.')
                  }
                </Alert>
              ) : (
                groupedProperties.map((group) => (
                  <Accordion 
                    key={group.aspectId} 
                    defaultExpanded 
                    sx={{ 
                      mb: 2,
                      '& .MuiAccordionSummary-root': {
                        backgroundColor: '#fafafa'
                      },
                      ...(group.aspectId === 'additional-properties' && {
                        border: '2px solid',
                        borderColor: 'info.main',
                        backgroundColor: '#f5f7fa',
                        '& .MuiAccordionSummary-root': {
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2'
                        }
                      })
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6">
                            {group.aspectName}
                          </Typography>
                          {group.aspectId === 'additional-properties' && (
                            <Chip
                              label={language === 'en' ? 'AI Suggestions' : 'Предложения ИИ'}
                              size="small"
                              color="info"
                              variant="filled"
                            />
                          )}
                        </Box>
                        {group.aspectDescription && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {group.aspectDescription}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {group.blocks.map((block) => (
                        <Box key={block.blockId} sx={{ mb: block !== group.blocks[group.blocks.length - 1] ? 3 : 0 }}>
                          <Typography variant="subtitle1" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                            {block.blockName}
                            <Chip 
                              label={block.properties.length} 
                              size="small" 
                              sx={{ ml: 1 }}
                              variant="outlined"
                            />
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>{language === 'en' ? 'Property' : 'Свойство'}</strong></TableCell>
                                  <TableCell><strong>{language === 'en' ? 'Value' : 'Значение'}</strong></TableCell>
                                  <TableCell><strong>{language === 'en' ? 'Type' : 'Тип'}</strong></TableCell>
                                  <TableCell><strong>{language === 'en' ? 'Confidence' : 'Уверенность'}</strong></TableCell>
                                  {group.aspectId === 'additional-properties' && (
                                    <TableCell><strong>{language === 'en' ? 'Reason' : 'Причина'}</strong></TableCell>
                                  )}
                                  <TableCell><strong>{language === 'en' ? 'Evidence' : 'Доказательства'}</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {block.properties.map(([propertyKey, propertyData]) => {
                                  const playbookProperty = playbook?.property_library?.[propertyKey];
                                  const propertyName = (propertyData as any).displayName || 
                                                     playbookProperty?.preferred_name?.[language] || 
                                                     playbookProperty?.preferred_name?.en || 
                                                     propertyKey;
                                  
                                  return (
                                    <TableRow key={propertyKey} hover>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                          {propertyName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', display: 'block', fontSize: '0.75rem' }}>
                                          {propertyKey}
                                        </Typography>
                                        {group.aspectId === 'additional-properties' && (propertyData as any).importance && (
                                          <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                              label={(propertyData as any).importance}
                                              size="small"
                                              variant="outlined"
                                              color={(propertyData as any).importance === 'high' ? 'error' : (propertyData as any).importance === 'medium' ? 'warning' : 'default'}
                                            />
                                          </Box>
                                        )}
                                        {(() => {
                                          const synonymSuggestions = getSynonymSuggestions(propertyKey);
                                          return synonymSuggestions.length > 0 && (
                                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mr: 1 }}>
                                                {language === 'en' ? 'Suggested synonyms:' : 'Синонимы из ТС:'}
                                              </Typography>
                                              {synonymSuggestions.map((synonym: string, index: number) => (
                                                <Chip
                                                  key={index}
                                                  label={synonym}
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{
                                                    fontSize: '0.65rem',
                                                    height: '18px',
                                                    borderColor: '#2196f3',
                                                    color: '#1976d2',
                                                    '& .MuiChip-label': {
                                                      px: 0.75
                                                    }
                                                  }}
                                                />
                                              ))}
                                            </Box>
                                          );
                                        })()}
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          {formatPropertyValue(propertyData.property)}
                                          {propertyData.property.kind && propertyData.property.kind !== 'exact' && (
                                            <Chip
                                              label={propertyData.property.kind}
                                              size="small"
                                              sx={{ ml: 1 }}
                                              variant="outlined"
                                            />
                                          )}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={propertyData.property.type}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={`${Math.round(propertyData.property.confidence * 100)}%`}
                                          size="small"
                                          color={getConfidenceColor(propertyData.property.confidence)}
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      {group.aspectId === 'additional-properties' && (
                                        <TableCell>
                                          {(propertyData as any).reason ? (
                                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                              {(propertyData as any).reason}
                                            </Typography>
                                          ) : '-'}
                                        </TableCell>
                                      )}
                                      <TableCell>
                                        {propertyData.property.evidence.length > 0 && (
                                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                            {propertyData.property.evidence[0].text}
                                            {propertyData.property.evidence.length > 1 && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block' }}
                                              >
                                                +{propertyData.property.evidence.length - 1} more
                                              </Typography>
                                            )}
                                          </Typography>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </TabPanel>
        ))}

        {/* Validation Tab */}
        <TabPanel value={activeTab} index={spec.items.length}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              {getValidationIcon(spec.validation.status)}
              <Typography variant="h6" sx={{ ml: 1 }}>
                Validation Status: {spec.validation.status.toUpperCase()}
              </Typography>
            </Box>

            {spec.validation.missing_required.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Missing Required Properties:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {spec.validation.missing_required.map((prop) => (
                    <Chip key={prop} label={prop} size="small" color="warning" />
                  ))}
                </Box>
              </Alert>
            )}

            {spec.validation.enum_violations.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Enum Violations:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {spec.validation.enum_violations.map((violation) => (
                    <Chip key={violation} label={violation} size="small" color="error" />
                  ))}
                </Box>
              </Alert>
            )}

            {spec.validation.status === 'ok' && 
             spec.validation.missing_required.length === 0 && 
             spec.validation.enum_violations.length === 0 && (
              <Alert severity="success">
                All validation checks passed successfully.
              </Alert>
            )}
          </Box>
        </TabPanel>

        {/* Full JSON Tab */}
        <TabPanel value={activeTab} index={spec.items.length + 1}>
          <Box sx={{ height: 600, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Editor
              height="100%"
              defaultLanguage="json"
              value={JSON.stringify(spec, null, 2)}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: 'on',
                folding: true,
                wordWrap: 'on',
              }}
            />
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default SpecViewer;
